/**
 * Dungeon Configurations
 * Configurações de dungeons para cada capítulo do modo história
 * Fase 1: Capítulo 2 como piloto
 */

import {
  DungeonConfig,
  DungeonRoom,
  DungeonExit,
  DoorDirection,
  RoomWaveConfig,
  PowerUpSpawn,
  EnvironmentHazard,
  DOOR_POSITIONS,
  DOOR_ROTATIONS,
  getOppositeDoorDirection,
  inferDoorDirectionFromRotation,
  pos,
} from "./dungeonTypes";
import { TERRAIN_THEMES } from "../waveGenerator";
import { BOSS_CONFIGS } from "./storyWaveGenerator";

// ============================================
// HELPERS PARA CRIAÇÃO DE DUNGEONS
// ============================================

/**
 * Cria uma saída (porta) padrão
 */
function createExit(
  id: string,
  direction: DoorDirection,
  targetRoomId: string,
  arenaSize: number,
  lockType: "enemies" | "none" | "key" | "secret" = "enemies",
  label?: string,
  options?: { requiredItemId?: string; lockedHint?: string }
): DungeonExit {
  return {
    id,
    direction,
    position: DOOR_POSITIONS[direction](arenaSize),
    rotation: DOOR_ROTATIONS[direction],
    targetRoomId,
    lockType,
    initialState: lockType === "none" ? "open" : "locked",
    label,
    requiredItemId: options?.requiredItemId,
    lockedHint: options?.lockedHint,
  };
}

function normalizeExitDirection(exit: DungeonExit): DoorDirection {
  if (exit.direction) return exit.direction;
  return inferDoorDirectionFromRotation(exit.rotation);
}

function createAutoReturnExit(
  sourceRoom: DungeonRoom,
  sourceExit: DungeonExit,
  targetRoom: DungeonRoom,
  idSuffix: number
): DungeonExit {
  const sourceDirection = normalizeExitDirection(sourceExit);
  const returnDirection = getOppositeDoorDirection(sourceDirection);
  return {
    id: `${sourceExit.id}__return_${idSuffix}`,
    direction: returnDirection,
    position: DOOR_POSITIONS[returnDirection](targetRoom.arenaSize),
    rotation: DOOR_ROTATIONS[returnDirection],
    targetRoomId: sourceRoom.id,
    lockType: "none",
    initialState: "open",
    isReturnPath: true,
    label: "Retorno",
  };
}

function normalizeDungeonConnections(config: DungeonConfig): DungeonConfig {
  const rooms = config.rooms.map((room) => ({
    ...room,
    exits: room.exits.map((exit) => ({
      ...exit,
      direction: normalizeExitDirection(exit),
      isReturnPath: exit.isReturnPath ?? false,
    })),
  }));

  const roomById = new Map<string, DungeonRoom>();
  for (const room of rooms) {
    roomById.set(room.id, room);
  }

  const terminalRoomIdsWithoutBoss = new Set<string>();
  if (!config.bossRoomId) {
    for (const room of rooms) {
      if (room.exits.length === 0) {
        terminalRoomIdsWithoutBoss.add(room.id);
      }
    }
  }

  let autoExitCounter = 0;
  for (const sourceRoom of rooms) {
    for (const exit of sourceRoom.exits) {
      const targetRoom = roomById.get(exit.targetRoomId);
      if (!targetRoom) continue;

      // Preserve terminal rooms without forward exits in non-boss dungeons.
      if (terminalRoomIdsWithoutBoss.has(targetRoom.id)) continue;

      const hasReturnConnection = targetRoom.exits.some(
        (targetExit) => targetExit.targetRoomId === sourceRoom.id
      );
      if (hasReturnConnection) continue;

      autoExitCounter += 1;
      targetRoom.exits = [
        createAutoReturnExit(sourceRoom, exit, targetRoom, autoExitCounter),
        ...targetRoom.exits,
      ];
    }
  }

  return {
    ...config,
    rooms,
  };
}

/**
 * Cria uma sala de combate padrão
 */
function createCombatRoom(
  id: string,
  name: string,
  arenaSize: number,
  waves: RoomWaveConfig[],
  exits: DungeonExit[],
  options?: {
    dialogueId?: string;
    hazards?: EnvironmentHazard[];
    powerUps?: PowerUpSpawn[];
    ambientColor?: string;
  }
): DungeonRoom {
  return {
    id,
    name,
    type: "combat",
    arenaSize,
    arenaShape: "square",
    terrainTheme: TERRAIN_THEMES.industrial,
    obstaclesDensity: 0.35,
    waves,
    exits,
    playerSpawnPosition: pos(0, 0, arenaSize * 0.4),
    ...options,
  };
}

/**
 * Cria um corredor de transição
 */
function createCorridor(
  id: string,
  name: string,
  exits: DungeonExit[],
  powerUps?: PowerUpSpawn[],
  dialogueId?: string
): DungeonRoom {
  return {
    id,
    name,
    type: "corridor",
    arenaSize: 25,
    arenaShape: "corridor",
    terrainTheme: TERRAIN_THEMES.industrial,
    obstaclesDensity: 0.1,
    waves: [], // Sem inimigos
    exits,
    powerUps: powerUps || [
      { type: "health", position: pos(0, 0, 0), spawnChance: 1 },
    ],
    dialogueId,
  };
}

/**
 * Cria uma sala de boss
 */
function createBossRoom(
  id: string,
  name: string,
  bossChapter: number,
  arenaSize: number,
  dialogueId?: string,
  grantsNarrativeItems?: string[]
): DungeonRoom {
  const bossConfig = BOSS_CONFIGS[bossChapter];
  
  return {
    id,
    name,
    type: "boss",
    arenaSize,
    arenaShape: "circle",
    terrainTheme: TERRAIN_THEMES.industrial,
    obstaclesDensity: 0.2,
    grantsNarrativeItems,
    waves: [
      {
        enemyCount: 1,
        enemyTypes: ["boss"],
        delayMs: 2000, // Tempo para diálogo
        bossConfig,
      },
    ],
    exits: [], // Vitória = fim do capítulo
    playerSpawnPosition: pos(0, 0, arenaSize * 0.35),
    dialogueId,
    ambientColor: "#ff3300",
    musicTrack: "boss_battle",
  };
}

// ============================================
// CAPÍTULO 1: TUTORIAL - MINI-DUNGEON
// Primeiro contato do jogador após o prólogo
// 3 salas progressivas de tutorial
// ============================================

export const CHAPTER_1_DUNGEON: DungeonConfig = {
  chapterId: 1,
  name: "Depósito Abandonado - Setor 7",
  description: "Após despertar o KITSUNE-00, Arthur enfrenta seus primeiros oponentes controlados pela GENESIS.",

  rooms: [
    // SALA 1: Tutorial de Movimento e Tiro (introdução com 2 waves)
    {
      id: "ch1-tutorial-1",
      name: "Área de Testes",
      type: "combat",
      arenaSize: 28, // Reduzido
      arenaShape: "square",
      terrainTheme: TERRAIN_THEMES.industrial,
      obstaclesDensity: 0.15, // Menos obstáculos
      waves: [
        { enemyCount: 2, enemyTypes: ["normal"], delayMs: 0 },
        { enemyCount: 1, enemyTypes: ["normal"], delayMs: 3000 },
      ],
      exits: [
        createExit("door-1", "NORTH", "ch1-tutorial-2", 28, "enemies", "Continuar"),
      ],
      playerSpawnPosition: pos(0, 0, 10),
      dialogueId: "chapter1_tutorial_movement",
    },

    // SALA 2: Corredor de Manutenção (mais inimigos, porta extra para tesouro)
    {
      id: "ch1-tutorial-2",
      name: "Corredor de Manutenção",
      type: "combat",
      arenaSize: 32, // Reduzido
      arenaShape: "corridor",
      terrainTheme: TERRAIN_THEMES.industrial,
      obstaclesDensity: 0.25,
      waves: [
        { enemyCount: 3, enemyTypes: ["normal"], delayMs: 0 },
        { enemyCount: 2, enemyTypes: ["normal", "fast"], delayMs: 3000 },
      ],
      exits: [
        createExit("door-2", "NORTH", "ch1-tutorial-corridor", 32, "enemies", "Saída"),
        createExit("door-2-secret", "EAST", "ch1-treasure", 32, "none", "Depósito"),
      ],
      playerSpawnPosition: pos(0, 0, 12),
      dialogueId: "chapter1_tutorial_dodge",
      hazards: [
        {
          type: "steam_vent",
          positions: [pos(-8, 0, 0), pos(8, 0, 0)],
          damage: 1, // Reduzido de 3 (tutorial)
          intervalMs: 4000,
          warningMs: 1200, // Janela maior de fuga
          radius: 2,
        },
      ],
    },

    // SALA SECRETA: Depósito de Suprimentos (treasure room)
    {
      id: "ch1-treasure",
      name: "Depósito de Suprimentos",
      type: "treasure" as const,
      arenaSize: 20,
      arenaShape: "square" as const,
      terrainTheme: TERRAIN_THEMES.industrial,
      obstaclesDensity: 0.1,
      waves: [],
      exits: [
        createExit("door-treasure-back", "WEST", "ch1-tutorial-2", 20, "none", "Voltar"),
      ],
      powerUps: [
        { type: "health", position: pos(-3, 0, 0), spawnChance: 1 },
        { type: "shield", position: pos(3, 0, 0), spawnChance: 1 },
      ],
    },

    // CORREDOR: Preparação antes do combate pesado
    createCorridor(
      "ch1-tutorial-corridor",
      "Corredor de Saída",
      [
        createExit("door-3", "NORTH", "ch1-tutorial-3", 25, "none", "Pátio Industrial"),
      ],
      [
        { type: "health", position: pos(0, 0, 0), spawnChance: 1 },
      ],
      "chapter1_nen_encouragement"
    ),

    // SALA 3: Pátio Industrial (Clímax do capítulo, sem heavies ou snipers)
    {
      id: "ch1-tutorial-3",
      name: "Pátio Industrial",
      type: "combat",
      arenaSize: 38, // Reduzido de 40
      arenaShape: "square",
      terrainTheme: TERRAIN_THEMES.industrial,
      obstaclesDensity: 0.35,
      waves: [
        { enemyCount: 3, enemyTypes: ["normal"], delayMs: 0 },
        { enemyCount: 3, enemyTypes: ["normal", "fast"], delayMs: 4000, difficultyMultiplier: 0.8 },
      ],
      exits: [], // Fim do capítulo! O diálogo "chapter1_final_wave" diz "Última barreira".
      playerSpawnPosition: pos(0, 0, 15),
      dialogueId: "chapter1_final_wave",
      hazards: [
        {
          type: "steam_vent",
          positions: [pos(-9, 0, -4), pos(9, 0, -4), pos(0, 0, 8)],
          damage: 2, // Reduzido
          intervalMs: 4200,
          warningMs: 1000,
          radius: 2.2,
        },
      ],
    },
  ],

  startRoomId: "ch1-tutorial-1",
  // Capítulo 1 não tem boss - bossRoomId opcional
  totalRooms: 5, // Atualizado de 6
  estimatedMinutes: 5, // Atualizado
  difficultyLevel: 1,

  completionRewards: [
    { type: "chips", amount: 200 },
    { type: "xp", amount: 100 },
  ],

  // Diálogos são disparados via dialogueId em cada sala
  secretRooms: [],
  version: "1.1.0",
};

// ============================================
// CAPÍTULO 2: ARENA INDUSTRIAL
// Primeiro dungeon do jogo - 3 salas lineares
// ============================================

export const CHAPTER_2_DUNGEON: DungeonConfig = {
  chapterId: 2,
  name: "Complexo Industrial Sigma-7",
  description: "Infiltre a base do Sargento RØK e derrote-o em seu próprio território.",
  
  rooms: [
    // SALA 1: Portão de Entrada
    createCombatRoom(
      "ch2-entrance",
      "Portão de Entrada",
      35,
      [
        { enemyCount: 3, enemyTypes: ["normal"], delayMs: 0 },
      ],
      [
        createExit("door-1", "NORTH", "ch2-factory", 35, "enemies", "Fábrica"),
      ],
      {
        dialogueId: "dungeon_entrance",
      }
    ),
    
    // SALA 2: Piso da Fábrica
    createCombatRoom(
      "ch2-factory",
      "Piso da Fábrica",
      40,
      [
        { enemyCount: 2, enemyTypes: ["normal", "fast"], delayMs: 0 },
        { enemyCount: 3, enemyTypes: ["normal", "heavy"], delayMs: 5000 },
      ],
      [
        createExit("door-2", "NORTH", "ch2-corridor", 40, "enemies", "Corredor"),
      ],
      {
        dialogueId: "dungeon_factory",
        hazards: [
          {
            type: "steam_vent",
            positions: [pos(-10, 0, 0), pos(10, 0, 0)],
            damage: 5,
            intervalMs: 3000,
            warningMs: 500,
            radius: 3,
          },
        ],
      }
    ),
    
    // CORREDOR: Manutenção (descanso antes do boss)
    createCorridor(
      "ch2-corridor",
      "Corredor de Manutenção",
      [
        createExit("door-3", "NORTH", "ch2-boss", 25, "none", "⚠️ BOSS"),
      ],
      [
        { type: "health", position: pos(-5, 0, 0), spawnChance: 1 },
        { type: "shield", position: pos(5, 0, 0), spawnChance: 0.5 },
      ],
      "dungeon_corridor"
    ),
    
    // SALA DO BOSS: Arena do Sargento RØK
    createBossRoom(
      "ch2-boss",
      "Arena do Sargento RØK",
      2,
      45,
      "dungeon_boss"
    ),
  ],
  
  startRoomId: "ch2-entrance",
  bossRoomId: "ch2-boss",
  totalRooms: 4,
  estimatedMinutes: 8,
  difficultyLevel: 3,
  
  completionRewards: [
    { type: "chips", amount: 300 },
    { type: "xp", amount: 600 },
    { type: "avatar", itemId: "sargento-rok" },
  ],
  
  version: "1.0.0",
};

// ============================================
// CAPÍTULO 3: DESERTO DE NEON (FUTURO)
// 4 salas com bifurcação opcional
// ============================================

export const CHAPTER_3_DUNGEON: DungeonConfig = {
  chapterId: 3,
  name: "Ruínas do Deserto de Neon",
  description: "Atravesse as ruínas do antigo império e enfrente a Capitã Nyx.",
  
  rooms: [
    // SALA 1: Entrada das Ruínas
    createCombatRoom(
      "ch3-entrance",
      "Entrada das Ruínas",
      35,
      [
        { enemyCount: 4, enemyTypes: ["normal", "fast"], delayMs: 0 },
      ],
      [
        createExit("door-1", "NORTH", "ch3-chamber", 35, "enemies"),
      ],
      {
        dialogueId: "chapter3_room1_intro",
      }
    ),
    
    // SALA 2: Câmara Central
    {
      id: "ch3-chamber",
      name: "Câmara Central",
      type: "combat",
      arenaSize: 45,
      arenaShape: "circle",
      terrainTheme: TERRAIN_THEMES.desert,
      obstaclesDensity: 0.4,
      waves: [
        { enemyCount: 3, enemyTypes: ["normal"], delayMs: 0 },
        { enemyCount: 2, enemyTypes: ["sniper"], delayMs: 4000 },
      ],
      exits: [
        createExit("door-2a", "NORTH", "ch3-corridor", 45, "enemies", "Corredor"),
        createExit("door-2b", "EAST", "ch3-treasure", 45, "enemies", "???"),
      ],
    },
    
    // SALA SECRETA: Tesouro (concede item narrativo Metroidvania)
    {
      id: "ch3-treasure",
      name: "Câmara do Tesouro",
      type: "treasure",
      arenaSize: 20,
      arenaShape: "square",
      terrainTheme: TERRAIN_THEMES.desert,
      obstaclesDensity: 0.1,
      waves: [], // Sem inimigos
      exits: [
        createExit("door-treasure-back", "WEST", "ch3-chamber", 20, "none"),
      ],
      powerUps: [
        { type: "health", position: pos(-3, 0, 0) },
        { type: "damage", position: pos(0, 0, 0) },
        { type: "shield", position: pos(3, 0, 0) },
      ],
      grantsNarrativeItems: ["neon_keycard"],
    },
    
    // CORREDOR
    createCorridor(
      "ch3-corridor",
      "Passagem Subterrânea",
      [
        createExit("door-3", "NORTH", "ch3-boss", 25, "none", "⚠️ BOSS"),
      ],
      [
        { type: "health", position: pos(0, 0, 0) },
      ],
      "chapter3_before_boss"
    ),
    
    // BOSS (concede item narrativo)
    createBossRoom(
      "ch3-boss",
      "Covil da Capitã Nyx",
      3,
      50,
      "chapter3_boss_intro",
      ["neon_keycard"]
    ),
  ],
  
  startRoomId: "ch3-entrance",
  bossRoomId: "ch3-boss",
  totalRooms: 4, // Não conta sala secreta no total obrigatório
  estimatedMinutes: 10,
  difficultyLevel: 4,
  
  completionRewards: [
    { type: "chips", amount: 400 },
    { type: "xp", amount: 800 },
    { type: "avatar", itemId: "capita-nyx" },
  ],
  
  secretRooms: [
    // A sala de tesouro pode ser descoberta
  ],
  
  version: "1.0.0",
};

// ============================================
// CAPÍTULO 4: FORTALEZA ÁRTICA
// 5 salas lineares + sala secreta
// ============================================

export const CHAPTER_4_DUNGEON: DungeonConfig = {
  chapterId: 4,
  name: "Fortaleza do Gelo Eterno",
  description: "Atravesse a fortaleza congelada do Comandante Boreal.",
  
  rooms: [
    // SALA 1: Entrada Gelada
    createCombatRoom(
      "ch4-entrance",
      "Portão Congelado",
      35,
      [
        { enemyCount: 3, enemyTypes: ["normal", "fast"], delayMs: 0 },
      ],
      [
        createExit("door-1", "NORTH", "ch4-frozen-hall", 35, "enemies", "Salão"),
      ],
      {
        dialogueId: "chapter4_room1_intro",
      }
    ),
    
    // SALA 2: Salão Congelado
    createCombatRoom(
      "ch4-frozen-hall",
      "Salão de Cristal",
      40,
      [
        { enemyCount: 3, enemyTypes: ["normal", "heavy"], delayMs: 0 },
        { enemyCount: 2, enemyTypes: ["sniper"], delayMs: 4000 },
      ],
      [
        createExit("door-2", "NORTH", "ch4-armory", 40, "enemies", "Arsenal"),
        createExit("door-secret", "EAST", "ch4-treasure", 40, "secret", "🔐 Passagem Cifrada", {
          requiredItemId: "neon_keycard",
          lockedHint: "Um leitor de cartão neon piscando. Você precisa de uma credencial da Nyx.",
        }),
      ],
      {
        hazards: [
          {
            type: "ice_patch",
            positions: [pos(-8, 0, -5), pos(8, 0, -5), pos(0, 0, 5)],
            damage: 0,
            intervalMs: 0,
            warningMs: 0,
            radius: 4,
          },
        ],
      }
    ),
    
    // SALA SECRETA: Tesouro (só acessível com neon_keycard do capítulo 3)
    {
      id: "ch4-treasure",
      name: "Cofre Cifrado",
      type: "treasure" as const,
      arenaSize: 20,
      arenaShape: "square" as const,
      terrainTheme: TERRAIN_THEMES.arctic,
      waves: [],
      exits: [
        createExit("door-back", "WEST", "ch4-frozen-hall", 20, "none"),
      ],
      powerUps: [
        { type: "damage", position: pos(-3, 0, 0), spawnChance: 1 },
        { type: "shield", position: pos(0, 0, 0), spawnChance: 1 },
        { type: "speed", position: pos(3, 0, 0), spawnChance: 1 },
      ],
      requiresNarrativeItem: "neon_keycard",
      grantsNarrativeItems: ["cryo_disruptor"],
    },
    
    // SALA 3: Arsenal
    createCombatRoom(
      "ch4-armory",
      "Arsenal Abandonado",
      40,
      [
        { enemyCount: 2, enemyTypes: ["heavy"], delayMs: 0 },
        { enemyCount: 3, enemyTypes: ["normal", "sniper"], delayMs: 5000 },
      ],
      [
        createExit("door-3", "NORTH", "ch4-corridor", 40, "enemies", "Corredor"),
      ],
      {}
    ),
    
    // CORREDOR
    createCorridor(
      "ch4-corridor",
      "Passagem Gelada",
      [
        createExit("door-4", "NORTH", "ch4-boss", 25, "none", "⚠️ BOSS"),
      ],
      [
        { type: "health", position: pos(-4, 0, 0) },
        { type: "shield", position: pos(4, 0, 0) },
      ],
      "chapter4_before_boss"
    ),
    
    // BOSS (concede item narrativo)
    createBossRoom(
      "ch4-boss",
      "Câmara do Comandante",
      4,
      50,
      "chapter4_boss_intro",
      ["cryo_disruptor"]
    ),
  ],
  
  startRoomId: "ch4-entrance",
  bossRoomId: "ch4-boss",
  totalRooms: 5,
  estimatedMinutes: 12,
  difficultyLevel: 5,
  
  completionRewards: [
    { type: "chips", amount: 500 },
    { type: "xp", amount: 1000 },
    { type: "avatar", itemId: "comandante-boreal" },
  ],
  
  secretRooms: [],
  version: "1.0.0",
};

// ============================================
// CAPÍTULO 5: FLORESTA SOMBRIA
// 5 salas com layout de labirinto
// ============================================

export const CHAPTER_5_DUNGEON: DungeonConfig = {
  chapterId: 5,
  name: "Floresta das Sombras",
  description: "Navegue pela floresta traiçoeira do General Verdant.",
  
  rooms: [
    // SALA 1: Entrada da Floresta
    createCombatRoom(
      "ch5-entrance",
      "Clareira de Entrada",
      35,
      [
        { enemyCount: 4, enemyTypes: ["fast"], delayMs: 0 },
      ],
      [
        createExit("door-1", "NORTH", "ch5-maze-a", 35, "enemies", "Trilha A"),
        createExit("door-2", "EAST", "ch5-maze-b", 35, "enemies", "Trilha B"),
      ],
      {
        dialogueId: "chapter5_room1_intro",
      }
    ),
    
    // SALA 2A: Trilha da Esquerda
    createCombatRoom(
      "ch5-maze-a",
      "Trilha Sombria",
      40,
      [
        { enemyCount: 3, enemyTypes: ["normal", "sniper"], delayMs: 0 },
        { enemyCount: 2, enemyTypes: ["heavy"], delayMs: 4000 },
      ],
      [
        createExit("door-3", "NORTH", "ch5-center", 40, "enemies", "Centro"),
      ],
      {
        hazards: [
          {
            type: "poison_cloud",
            positions: [pos(-5, 0, 0), pos(5, 0, -8)],
            damage: 3,
            intervalMs: 2000,
            warningMs: 500,
            radius: 5,
          },
        ],
      }
    ),
    
    // SALA 2B: Trilha da Direita
    createCombatRoom(
      "ch5-maze-b",
      "Trilha Espinhosa",
      40,
      [
        { enemyCount: 5, enemyTypes: ["fast", "normal"], delayMs: 0 },
      ],
      [
        createExit("door-3b", "WEST", "ch5-center", 40, "enemies", "Centro"),
      ],
      {}
    ),
    
    // SALA 3: Centro do Labirinto
    createCombatRoom(
      "ch5-center",
      "Coração da Floresta",
      45,
      [
        { enemyCount: 3, enemyTypes: ["heavy", "sniper"], delayMs: 0 },
        { enemyCount: 4, enemyTypes: ["fast", "normal"], delayMs: 5000 },
      ],
      [
        createExit("door-4", "NORTH", "ch5-corridor", 45, "enemies", "Saída"),
      ],
      {}
    ),
    
    // CORREDOR
    createCorridor(
      "ch5-corridor",
      "Caminho Final",
      [
        createExit("door-5", "NORTH", "ch5-boss", 25, "none", "⚠️ BOSS"),
      ],
      [
        { type: "health", position: pos(0, 0, -5) },
        { type: "damage", position: pos(0, 0, 5) },
      ],
      "chapter5_before_boss"
    ),
    
    // BOSS (concede item narrativo)
    createBossRoom(
      "ch5-boss",
      "Covil do General",
      5,
      50,
      "chapter5_boss_intro",
      ["verdant_spore"]
    ),
  ],
  
  startRoomId: "ch5-entrance",
  bossRoomId: "ch5-boss",
  totalRooms: 5,
  estimatedMinutes: 15,
  difficultyLevel: 6,
  
  completionRewards: [
    { type: "chips", amount: 600 },
    { type: "xp", amount: 1200 },
    { type: "avatar", itemId: "general-verdant" },
  ],
  
  secretRooms: [],
  version: "1.0.0",
};

// ============================================
// CAPÍTULO 6: NÚCLEO VULCÂNICO
// 6 salas épicas - confronto final
// ============================================

export const CHAPTER_6_DUNGEON: DungeonConfig = {
  chapterId: 6,
  name: "Núcleo de Arcádia",
  description: "Enfrente o TITÃ-AXIS no coração de Arcádia-7.",
  
  rooms: [
    // SALA 1: Entrada do Vulcão
    createCombatRoom(
      "ch6-entrance",
      "Portão do Vulcão",
      40,
      [
        { enemyCount: 4, enemyTypes: ["normal", "heavy"], delayMs: 0 },
      ],
      [
        createExit("door-1", "NORTH", "ch6-lava-bridge", 40, "enemies", "Ponte"),
      ],
      {
        dialogueId: "chapter6_room1_intro",
      }
    ),
    
    // SALA 2: Ponte de Lava
    createCombatRoom(
      "ch6-lava-bridge",
      "Ponte Incandescente",
      45,
      [
        { enemyCount: 3, enemyTypes: ["fast", "sniper"], delayMs: 0 },
        { enemyCount: 3, enemyTypes: ["heavy"], delayMs: 4000 },
      ],
      [
        createExit("door-2", "NORTH", "ch6-forge", 45, "enemies", "Forja"),
      ],
      {
        hazards: [
          {
            type: "lava_geyser",
            positions: [pos(-10, 0, -5), pos(10, 0, -5), pos(0, 0, 5)],
            damage: 15,
            intervalMs: 4000,
            warningMs: 1000,
            radius: 4,
          },
        ],
      }
    ),
    
    // SALA 3: Forja Antiga
    createCombatRoom(
      "ch6-forge",
      "Forja dos Titãs",
      45,
      [
        { enemyCount: 4, enemyTypes: ["heavy", "heavy"], delayMs: 0 },
        { enemyCount: 2, enemyTypes: ["sniper"], delayMs: 5000 },
      ],
      [
        createExit("door-3", "NORTH", "ch6-antechamber", 45, "enemies", "Ante-sala"),
      ],
      {}
    ),
    
    // SALA 4: Ante-sala do Titã
    createCombatRoom(
      "ch6-antechamber",
      "Ante-sala do Núcleo",
      50,
      [
        { enemyCount: 3, enemyTypes: ["heavy", "sniper"], delayMs: 0 },
        { enemyCount: 4, enemyTypes: ["fast", "normal"], delayMs: 4000 },
        { enemyCount: 2, enemyTypes: ["heavy"], delayMs: 8000 },
      ],
      [
        createExit("door-4", "NORTH", "ch6-corridor", 50, "enemies", "Corredor"),
      ],
      {}
    ),
    
    // CORREDOR FINAL
    createCorridor(
      "ch6-corridor",
      "Passagem do Destino",
      [
        createExit("door-5", "NORTH", "ch6-boss", 30, "none", "⚠️ BOSS FINAL"),
      ],
      [
        { type: "health", position: pos(-6, 0, 0) },
        { type: "health", position: pos(0, 0, 0) },
        { type: "shield", position: pos(6, 0, 0) },
      ],
      "chapter6_before_boss"
    ),
    
    // BOSS FINAL (concede item narrativo para capítulos posteriores)
    createBossRoom(
      "ch6-boss",
      "Núcleo do TITÃ-AXIS",
      6,
      60,
      "chapter6_boss_intro",
      ["titan_core_fragment"]
    ),
  ],
  
  startRoomId: "ch6-entrance",
  bossRoomId: "ch6-boss",
  totalRooms: 6,
  estimatedMinutes: 20,
  difficultyLevel: 8,
  
  completionRewards: [
    { type: "chips", amount: 1000 },
    { type: "xp", amount: 2000 },
    { type: "avatar", itemId: "titan-axis" },
    { type: "skin", itemId: "volcanic-champion" },
  ],
  
  secretRooms: [],
  version: "1.0.0",
};

// ============================================
// CAPÍTULO 7: ESTAÇÃO ORBITAL - SOMBRAS DO PASSADO
// 7 salas, Metroidvania: requer titan_core_fragment (cap 6) para caminho
// alternativo; concede memory_core ao derrotar boss ECO-ZERO.
// Layout em Y (duas rotas que se juntam na ante-sala do boss).
// ============================================

export const CHAPTER_7_DUNGEON: DungeonConfig = {
  chapterId: 7,
  name: "Estação Orbital Eclipse",
  description: "Fragmentos da memória de NEN-0 levam Arthur a uma estação orbital abandonada. O primeiro protótipo ainda opera lá.",

  rooms: [
    // SALA 1: Docas (entrada)
    createCombatRoom(
      "ch7-docks",
      "Docas de Atracação",
      38,
      [
        { enemyCount: 4, enemyTypes: ["normal", "fast"], delayMs: 0 },
      ],
      [
        createExit("ch7-d1", "NORTH", "ch7-atrium", 38, "enemies", "Átrio"),
      ],
      { dialogueId: "chapter7_room1_intro", ambientColor: "#1e293b" }
    ),

    // SALA 2: Átrio central — bifurcação
    {
      id: "ch7-atrium",
      name: "Átrio Central",
      type: "combat",
      arenaSize: 48,
      arenaShape: "circle",
      terrainTheme: TERRAIN_THEMES.cyber,
      obstaclesDensity: 0.3,
      waves: [
        { enemyCount: 3, enemyTypes: ["normal", "sniper"], delayMs: 0 },
        { enemyCount: 3, enemyTypes: ["heavy"], delayMs: 5000 },
      ],
      exits: [
        // Rota padrão (sempre disponível)
        createExit("ch7-d2a", "NORTH", "ch7-maintenance", 48, "enemies", "Manutenção"),
        // Rota alternativa (requer fragmento do Titã)
        createExit("ch7-d2b", "EAST", "ch7-core-access", 48, "secret", "🔥 Corredor de Energia", {
          requiredItemId: "titan_core_fragment",
          lockedHint: "Uma barreira de plasma ativa. Apenas energia do Titã pode desestabilizá-la.",
        }),
      ],
      playerSpawnPosition: pos(0, 0, 18),
    },

    // SALA 3A: Rota padrão — Manutenção
    createCombatRoom(
      "ch7-maintenance",
      "Corredor de Manutenção",
      40,
      [
        { enemyCount: 4, enemyTypes: ["fast", "normal"], delayMs: 0 },
        { enemyCount: 3, enemyTypes: ["sniper"], delayMs: 4000 },
      ],
      [
        createExit("ch7-d3", "NORTH", "ch7-antechamber", 40, "enemies", "Ante-sala"),
      ],
      {
        hazards: [
          {
            type: "electric_floor",
            positions: [pos(-8, 0, 0), pos(8, 0, 0)],
            damage: 6,
            intervalMs: 3000,
            warningMs: 800,
            radius: 3,
          },
        ],
      }
    ),

    // SALA 3B: Rota alternativa — Acesso ao núcleo (concede arctic_relic bonus)
    {
      id: "ch7-core-access",
      name: "Câmara do Núcleo",
      type: "treasure",
      arenaSize: 28,
      arenaShape: "square",
      terrainTheme: TERRAIN_THEMES.cyber,
      obstaclesDensity: 0.15,
      waves: [],
      exits: [
        createExit("ch7-d-back", "WEST", "ch7-atrium", 28, "none", "Voltar"),
        createExit("ch7-d-bonus", "NORTH", "ch7-antechamber", 28, "none", "Atalho"),
      ],
      powerUps: [
        { type: "damage", position: pos(-3, 0, 0) },
        { type: "shield", position: pos(0, 0, 0) },
        { type: "health", position: pos(3, 0, 0) },
      ],
      grantsNarrativeItems: ["arctic_relic"], // bônus por quem traz o fragmento do Titã
    },

    // SALA 4: Ante-sala (corredor antes do boss)
    createCorridor(
      "ch7-antechamber",
      "Ante-sala do Protótipo",
      [
        createExit("ch7-d4", "NORTH", "ch7-boss", 25, "none", "⚠️ BOSS"),
      ],
      [
        { type: "health", position: pos(-4, 0, 0) },
        { type: "shield", position: pos(4, 0, 0) },
      ],
      "chapter7_before_boss"
    ),

    // BOSS: ECO-ZERO — concede memory_core
    createBossRoom(
      "ch7-boss",
      "Câmara do Protótipo",
      7,
      52,
      "chapter7_boss_intro",
      ["memory_core"]
    ),
  ],

  startRoomId: "ch7-docks",
  bossRoomId: "ch7-boss",
  totalRooms: 5,
  estimatedMinutes: 14,
  difficultyLevel: 7,

  completionRewards: [
    { type: "chips", amount: 700 },
    { type: "xp", amount: 1400 },
    { type: "avatar", itemId: "echo-zero" },
  ],

  secretRooms: [],
  version: "1.0.0",
};

// ============================================
// CAPÍTULO 8: RUÍNAS POLARES - GELO E SILÊNCIO
// 6 salas, Metroidvania: requer memory_core (cap 7) para sala opcional,
// cryo_disruptor (cap 4) para caminho curto ao boss.
// Concede arctic_relic (chave de cap 9) ao derrotar GELO-MESTRE.
// ============================================

export const CHAPTER_8_DUNGEON: DungeonConfig = {
  chapterId: 8,
  name: "Ruínas Polares Krios",
  description: "Artefatos do antigo comando polar guardam o próximo passo. Mas o gelo nunca esquece.",

  rooms: [
    // SALA 1: Entrada gélida
    createCombatRoom(
      "ch8-entrance",
      "Portão de Krios",
      40,
      [
        { enemyCount: 4, enemyTypes: ["normal", "heavy"], delayMs: 0 },
      ],
      [
        createExit("ch8-d1", "NORTH", "ch8-archive", 40, "enemies", "Arquivo"),
      ],
      { dialogueId: "chapter8_room1_intro" }
    ),

    // SALA 2: Arquivo congelado — bifurcação
    {
      id: "ch8-archive",
      name: "Arquivo Congelado",
      type: "combat",
      arenaSize: 46,
      arenaShape: "cross",
      terrainTheme: TERRAIN_THEMES.arctic,
      obstaclesDensity: 0.4,
      waves: [
        { enemyCount: 4, enemyTypes: ["sniper", "fast"], delayMs: 0 },
        { enemyCount: 3, enemyTypes: ["heavy"], delayMs: 5000 },
      ],
      exits: [
        // Caminho longo (padrão)
        createExit("ch8-d2a", "NORTH", "ch8-tunnels", 46, "enemies", "Túneis"),
        // Atalho (requer cryo_disruptor)
        createExit("ch8-d2b", "WEST", "ch8-shortcut", 46, "secret", "❄️ Barreira de Gelo", {
          requiredItemId: "cryo_disruptor",
          lockedHint: "Uma parede de gelo eterno. Um disruptor crio poderia parti-la.",
        }),
        // Sala opcional (requer memory_core)
        createExit("ch8-d2c", "EAST", "ch8-memory-vault", 46, "secret", "🧠 Cofre de Memória", {
          requiredItemId: "memory_core",
          lockedHint: "Uma fechadura neural. Precisa de um núcleo de memória compatível.",
        }),
      ],
      hazards: [
        {
          type: "ice_patch",
          positions: [pos(-10, 0, -5), pos(10, 0, -5), pos(0, 0, 5)],
          damage: 0,
          intervalMs: 0,
          warningMs: 0,
          radius: 4,
        },
      ],
    },

    // SALA 3: Túneis (rota longa)
    createCombatRoom(
      "ch8-tunnels",
      "Túneis Glaciais",
      42,
      [
        { enemyCount: 5, enemyTypes: ["heavy", "normal"], delayMs: 0 },
        { enemyCount: 3, enemyTypes: ["sniper"], delayMs: 5000 },
      ],
      [
        createExit("ch8-d3", "NORTH", "ch8-antechamber", 42, "enemies", "Ante-sala"),
      ],
      {}
    ),

    // SALA 3-alt: Atalho (acessível com cryo_disruptor)
    createCorridor(
      "ch8-shortcut",
      "Passagem Derretida",
      [
        createExit("ch8-dsc", "NORTH", "ch8-antechamber", 25, "none", "Atalho"),
        createExit("ch8-dsc-back", "EAST", "ch8-archive", 25, "none", "Voltar"),
      ],
      [
        { type: "health", position: pos(0, 0, 0) },
        { type: "shield", position: pos(3, 0, 0) },
      ]
    ),

    // SALA OPCIONAL: Cofre de Memória (recompensa extra)
    {
      id: "ch8-memory-vault",
      name: "Cofre da Relíquia",
      type: "treasure",
      arenaSize: 22,
      arenaShape: "square",
      terrainTheme: TERRAIN_THEMES.arctic,
      waves: [],
      exits: [
        createExit("ch8-dmv-back", "WEST", "ch8-archive", 22, "none", "Voltar"),
      ],
      powerUps: [
        { type: "damage", position: pos(-3, 0, 0) },
        { type: "shield", position: pos(0, 0, 0) },
        { type: "speed", position: pos(3, 0, 0) },
      ],
    },

    // CORREDOR: Ante-sala do Boss
    createCorridor(
      "ch8-antechamber",
      "Ante-sala Glacial",
      [
        createExit("ch8-d4", "NORTH", "ch8-boss", 25, "none", "⚠️ BOSS"),
      ],
      [
        { type: "health", position: pos(-4, 0, 0) },
        { type: "shield", position: pos(4, 0, 0) },
      ],
      "chapter8_before_boss"
    ),

    // BOSS: GELO-MESTRE — concede arctic_relic
    createBossRoom(
      "ch8-boss",
      "Coração Congelado",
      8,
      54,
      "chapter8_boss_intro",
      ["arctic_relic"]
    ),
  ],

  startRoomId: "ch8-entrance",
  bossRoomId: "ch8-boss",
  totalRooms: 6,
  estimatedMinutes: 16,
  difficultyLevel: 8,

  completionRewards: [
    { type: "chips", amount: 800 },
    { type: "xp", amount: 1600 },
    { type: "avatar", itemId: "glacier-master" },
  ],

  secretRooms: [],
  version: "1.0.0",
};

// ============================================
// CAPÍTULO 9: COVIL DO TRAIDOR - CORVUS-7
// 7 salas em 2 níveis (labirinto). Metroidvania: requer memory_core e
// arctic_relic para acessar a antecâmara; verdant_spore abre rota secreta.
// Concede corvus_masterkey ao derrotar CORVUS-7.
// ============================================

export const CHAPTER_9_DUNGEON: DungeonConfig = {
  chapterId: 9,
  name: "Covil Sombrio de CORVUS-7",
  description: "O traidor esperava por vocês. Seu covil é um labirinto de paranoia e firewalls vivos.",

  rooms: [
    // SALA 1: Entrada
    createCombatRoom(
      "ch9-entrance",
      "Portal Cifrado",
      42,
      [
        { enemyCount: 5, enemyTypes: ["fast", "normal"], delayMs: 0 },
      ],
      [
        createExit("ch9-d1", "NORTH", "ch9-nexus", 42, "enemies", "Nexus"),
      ],
      { dialogueId: "chapter9_room1_intro", ambientColor: "#1e1b4b" }
    ),

    // SALA 2: Nexus — hub central com três caminhos
    {
      id: "ch9-nexus",
      name: "Nexus Sombrio",
      type: "combat",
      arenaSize: 52,
      arenaShape: "maze",
      terrainTheme: TERRAIN_THEMES.cyber,
      obstaclesDensity: 0.45,
      waves: [
        { enemyCount: 4, enemyTypes: ["sniper", "heavy"], delayMs: 0 },
        { enemyCount: 4, enemyTypes: ["fast", "normal"], delayMs: 5000 },
      ],
      exits: [
        // Caminho A: firewall (padrão)
        createExit("ch9-d2a", "NORTH", "ch9-firewall", 52, "enemies", "Firewall"),
        // Caminho B: pátio sombrio (requer verdant_spore)
        createExit("ch9-d2b", "EAST", "ch9-garden", 52, "secret", "🌿 Raízes Negras", {
          requiredItemId: "verdant_spore",
          lockedHint: "Raízes corruptas bloqueiam o caminho. Um esporo Verdant poderia dispersá-las.",
        }),
      ],
    },

    // SALA 3A: Firewall
    createCombatRoom(
      "ch9-firewall",
      "Firewall Vivo",
      46,
      [
        { enemyCount: 4, enemyTypes: ["heavy"], delayMs: 0 },
        { enemyCount: 4, enemyTypes: ["sniper", "fast"], delayMs: 5000 },
      ],
      [
        createExit("ch9-d3", "NORTH", "ch9-gate", 46, "enemies", "Portão"),
      ],
      {
        hazards: [
          {
            type: "electric_floor",
            positions: [pos(-10, 0, 0), pos(10, 0, 0), pos(0, 0, -8)],
            damage: 8,
            intervalMs: 3000,
            warningMs: 700,
            radius: 3,
          },
        ],
      }
    ),

    // SALA 3B: Jardim sombrio (rota alternativa)
    createCombatRoom(
      "ch9-garden",
      "Jardim da Corrupção",
      40,
      [
        { enemyCount: 5, enemyTypes: ["fast", "normal"], delayMs: 0 },
      ],
      [
        createExit("ch9-d-gb", "NORTH", "ch9-gate", 40, "enemies", "Portão"),
        createExit("ch9-d-gb-back", "WEST", "ch9-nexus", 40, "none", "Voltar"),
      ],
      {
        hazards: [
          {
            type: "poison_cloud",
            positions: [pos(-5, 0, 0), pos(5, 0, -6)],
            damage: 4,
            intervalMs: 2000,
            warningMs: 600,
            radius: 4,
          },
        ],
      }
    ),

    // SALA 4: Portão duplo — requer dois itens
    {
      id: "ch9-gate",
      name: "Portão dos Selos",
      type: "combat",
      arenaSize: 44,
      arenaShape: "circle",
      terrainTheme: TERRAIN_THEMES.cyber,
      obstaclesDensity: 0.3,
      waves: [
        { enemyCount: 3, enemyTypes: ["heavy", "sniper"], delayMs: 0 },
      ],
      exits: [
        // Porta dupla: requer MEMORY_CORE (cap 7) + ARCTIC_RELIC (cap 8).
        // Implementação atual suporta 1 requiredItemId por porta; usamos uma
        // sequência de duas portas (memória → relíquia) para simular dupla chave.
        createExit("ch9-d4a", "NORTH", "ch9-memorylock", 44, "secret", "🧠 Selo de Memória", {
          requiredItemId: "memory_core",
          lockedHint: "Selo neural. Requer um núcleo de memória compatível.",
        }),
      ],
    },

    // SALA 5: Selo de Relíquia
    {
      id: "ch9-memorylock",
      name: "Corredor dos Selos",
      type: "corridor",
      arenaSize: 28,
      arenaShape: "corridor",
      terrainTheme: TERRAIN_THEMES.cyber,
      obstaclesDensity: 0.1,
      waves: [],
      exits: [
        createExit("ch9-d5", "NORTH", "ch9-boss", 28, "secret", "🧊 Selo Ártico", {
          requiredItemId: "arctic_relic",
          lockedHint: "Selo ártico. Apenas uma relíquia polar abrirá este portão.",
        }),
      ],
      powerUps: [
        { type: "health", position: pos(-4, 0, 0) },
        { type: "shield", position: pos(4, 0, 0) },
      ],
      dialogueId: "chapter9_before_boss",
    },

    // BOSS: CORVUS-7 — concede corvus_masterkey
    createBossRoom(
      "ch9-boss",
      "Trono de CORVUS",
      9,
      58,
      "chapter9_boss_intro",
      ["corvus_masterkey"]
    ),
  ],

  startRoomId: "ch9-entrance",
  bossRoomId: "ch9-boss",
  totalRooms: 7,
  estimatedMinutes: 18,
  difficultyLevel: 9,

  completionRewards: [
    { type: "chips", amount: 900 },
    { type: "xp", amount: 1800 },
    { type: "avatar", itemId: "corvus-7" },
    { type: "skin", itemId: "shadow-master" },
  ],

  secretRooms: [],
  version: "1.0.0",
};

// ============================================
// CAPÍTULO 10: PROMETHEUS - CONFRONTO FINAL
// 7 salas, confronto final. Metroidvania: requer corvus_masterkey (cap 9)
// para o portão final. titan_core_fragment (cap 6) abre ala opcional de
// armas. Boss KITSUNE-PRIME com 3 fases.
// ============================================

export const CHAPTER_10_DUNGEON: DungeonConfig = {
  chapterId: 10,
  name: "Forja Prometheus",
  description: "Onde tudo começou. Onde tudo termina. Dr. Blackwell desperta o KITSUNE-PRIME.",

  rooms: [
    // SALA 1: Entrada da Forja
    createCombatRoom(
      "ch10-landing",
      "Plataforma de Descida",
      42,
      [
        { enemyCount: 5, enemyTypes: ["fast", "heavy"], delayMs: 0 },
      ],
      [
        createExit("ch10-d1", "NORTH", "ch10-foundry", 42, "enemies", "Fundição"),
      ],
      { dialogueId: "chapter10_room1_intro", ambientColor: "#7f1d1d" }
    ),

    // SALA 2: Fundição — bifurcação com ala opcional
    {
      id: "ch10-foundry",
      name: "Fundição Vulcânica",
      type: "combat",
      arenaSize: 50,
      arenaShape: "circle",
      terrainTheme: TERRAIN_THEMES.volcanic,
      obstaclesDensity: 0.4,
      waves: [
        { enemyCount: 4, enemyTypes: ["heavy"], delayMs: 0 },
        { enemyCount: 4, enemyTypes: ["sniper", "fast"], delayMs: 5000 },
      ],
      exits: [
        // Rota principal
        createExit("ch10-d2a", "NORTH", "ch10-assembly", 50, "enemies", "Montagem"),
        // Ala opcional (requer titan_core_fragment)
        createExit("ch10-d2b", "EAST", "ch10-armory", 50, "secret", "🔥 Arsenal Titã", {
          requiredItemId: "titan_core_fragment",
          lockedHint: "Uma porta selada com circuitos do Titã. Você precisa do fragmento.",
        }),
      ],
      hazards: [
        {
          type: "lava_geyser",
          positions: [pos(-12, 0, -6), pos(12, 0, -6), pos(0, 0, 8)],
          damage: 18,
          intervalMs: 4000,
          warningMs: 1000,
          radius: 4,
        },
      ],
    },

    // SALA 2-ALT: Arsenal opcional
    {
      id: "ch10-armory",
      name: "Arsenal do Titã",
      type: "treasure",
      arenaSize: 26,
      arenaShape: "square",
      terrainTheme: TERRAIN_THEMES.volcanic,
      waves: [],
      exits: [
        createExit("ch10-d-ab", "WEST", "ch10-foundry", 26, "none", "Voltar"),
      ],
      powerUps: [
        { type: "damage", position: pos(-4, 0, 0) },
        { type: "damage", position: pos(0, 0, 0) },
        { type: "shield", position: pos(4, 0, 0) },
      ],
    },

    // SALA 3: Linha de Montagem
    createCombatRoom(
      "ch10-assembly",
      "Linha de Montagem",
      48,
      [
        { enemyCount: 4, enemyTypes: ["heavy", "sniper"], delayMs: 0 },
        { enemyCount: 5, enemyTypes: ["fast", "normal"], delayMs: 5000 },
        { enemyCount: 3, enemyTypes: ["heavy"], delayMs: 10000 },
      ],
      [
        createExit("ch10-d3", "NORTH", "ch10-core-path", 48, "enemies", "Caminho do Núcleo"),
      ],
      {}
    ),

    // SALA 4: Caminho do Núcleo (selo Corvus)
    {
      id: "ch10-core-path",
      name: "Caminho do Núcleo",
      type: "corridor",
      arenaSize: 28,
      arenaShape: "corridor",
      terrainTheme: TERRAIN_THEMES.volcanic,
      obstaclesDensity: 0.1,
      waves: [],
      exits: [
        createExit("ch10-d4", "NORTH", "ch10-antechamber", 28, "secret", "🖤 Selo CORVUS", {
          requiredItemId: "corvus_masterkey",
          lockedHint: "Apenas a chave mestra de CORVUS rompe este selo final.",
        }),
      ],
      powerUps: [
        { type: "health", position: pos(-4, 0, 0) },
        { type: "shield", position: pos(4, 0, 0) },
      ],
    },

    // CORREDOR: Ante-sala do Boss Final
    createCorridor(
      "ch10-antechamber",
      "Ante-sala do KITSUNE-PRIME",
      [
        createExit("ch10-d5", "NORTH", "ch10-boss", 25, "none", "⚠️ BOSS FINAL"),
      ],
      [
        { type: "health", position: pos(-6, 0, 0) },
        { type: "health", position: pos(0, 0, 0) },
        { type: "shield", position: pos(6, 0, 0) },
      ],
      "chapter10_before_boss"
    ),

    // BOSS FINAL: KITSUNE-PRIME (3 fases)
    createBossRoom(
      "ch10-boss",
      "Câmara do PRIME",
      10,
      62,
      "chapter10_boss_intro"
    ),
  ],

  startRoomId: "ch10-landing",
  bossRoomId: "ch10-boss",
  totalRooms: 7,
  estimatedMinutes: 25,
  difficultyLevel: 10,

  completionRewards: [
    { type: "chips", amount: 1500 },
    { type: "xp", amount: 3000 },
    { type: "avatar", itemId: "kitsune-prime" },
    { type: "skin", itemId: "prime-champion" },
    { type: "title", itemId: "legend-of-arcadia" },
  ],

  secretRooms: [],
  version: "1.0.0",
};

export const CHAPTER_DUNGEONS: Record<number, DungeonConfig | null> = {
  1: normalizeDungeonConnections(CHAPTER_1_DUNGEON), // Tutorial - Mini-dungeon com prólogo
  2: normalizeDungeonConnections(CHAPTER_2_DUNGEON),
  3: normalizeDungeonConnections(CHAPTER_3_DUNGEON),
  4: normalizeDungeonConnections(CHAPTER_4_DUNGEON),
  5: normalizeDungeonConnections(CHAPTER_5_DUNGEON),
  6: normalizeDungeonConnections(CHAPTER_6_DUNGEON),
  7: normalizeDungeonConnections(CHAPTER_7_DUNGEON),
  8: normalizeDungeonConnections(CHAPTER_8_DUNGEON),
  9: normalizeDungeonConnections(CHAPTER_9_DUNGEON),
  10: normalizeDungeonConnections(CHAPTER_10_DUNGEON),
};

// ============================================
// FUNÇÕES UTILITÁRIAS
// ============================================

/**
 * Verifica se um capítulo usa o sistema de dungeon
 */
export function chapterHasDungeon(chapterId: number): boolean {
  return CHAPTER_DUNGEONS[chapterId] !== null && CHAPTER_DUNGEONS[chapterId] !== undefined;
}

/**
 * Obtém a configuração do dungeon para um capítulo
 */
export function getDungeonConfig(chapterId: number): DungeonConfig | null {
  return CHAPTER_DUNGEONS[chapterId] || null;
}

/**
 * Obtém informações resumidas do dungeon para exibição
 */
export function getDungeonSummary(chapterId: number): {
  name: string;
  rooms: number;
  estimatedTime: number;
  difficulty: number;
} | null {
  const dungeon = CHAPTER_DUNGEONS[chapterId];
  if (!dungeon) return null;
  
  return {
    name: dungeon.name,
    rooms: dungeon.totalRooms,
    estimatedTime: dungeon.estimatedMinutes,
    difficulty: dungeon.difficultyLevel,
  };
}

/**
 * Gera inimigos para uma sala específica do dungeon
 * Integra com o sistema existente de storyWaveGenerator
 */
export function getDungeonRoomWave(
  dungeon: DungeonConfig,
  roomId: string,
  waveIndex: number
): RoomWaveConfig | null {
  const room = dungeon.rooms.find(r => r.id === roomId);
  if (!room || waveIndex >= room.waves.length) return null;
  
  return room.waves[waveIndex];
}
